import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tabs } from '@material-ui/core';

import Helmet from 'react-helmet';
import moment from 'moment';
import { convertObjToArr, sortArrByKey } from '../../utils';
import SectionCard from '../../components/SectionCard';
import MsgBox from '../../components/MsgBox';
import Course from './Course';
import Comments from './Comments';
import Notes from './Notes';
import { TabTitle, TabPanel } from './TabsWrappers';
import useStyles from './styles';

import Actions from '../../store/ducks/course';
import ProfileActions from '../../store/ducks/userProfile';

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    comments,
    annotations,
    product: { productName, progressLabel },
    course: {
      list: coursesObj,
      allCoursesLoaded,
      loading: { list: requestingCourses },
      error: { list: listCoursesError },
    },
  } = useSelector(state => state);

  const [activeCourse, setActiveCourse] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [coursesArr, setCoursesArr] = useState([]);
  const [productWithComments, setProductWithComments] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const { accessProfiles } = useSelector(({ userProfile }) => userProfile);

  const commentsAmmount = useMemo(() => {
    return Object.keys(comments.studentComments.list).length || 0;
  }, [comments.studentComments.list]);

  useEffect(() => {
    if (!accessProfiles?.list) {
      dispatch(ProfileActions.getAccessProfiles());
    }
  }, [accessProfiles?.list, dispatch]);

  const activeProfile = useMemo(() => {
    if (accessProfiles?.list) {
      const profile = Object.values(accessProfiles?.list).find(
        item => item.productName === productName,
      );
      return profile;
    }
    return null;
  }, [accessProfiles?.list, productName]);

  const studentProfileData = useMemo(() => {
    if (activeProfile) {
      const createdAt = moment(activeProfile.createdAt);

      let productAccessExpiration;

      if (
        !!activeProfile.productAccessExpirationUnity &&
        !!activeProfile.productAccessExpiration
      ) {
        const period =
          activeProfile.productAccessExpirationUnity === 'MM'
            ? 'months'
            : 'days';

        productAccessExpiration = moment(createdAt)
          .add(activeProfile.productAccessExpiration, period)
          .add(activeProfile.extraAccessMonths, 'months')
          .format('DD/MM/YYYY');
      }

      return {
        createdAt: moment(createdAt).format('DD/MM/YYYY'),
        productAccessExpiration,
      };
    }
    return null;
  }, [activeProfile]);

  const annotationsAmmount = useMemo(() => {
    return Object.keys(annotations.list).length || 0;
  }, [annotations.list]);

  useEffect(() => {
    setLoadingCourses(
      requestingCourses || (!allCoursesLoaded && !listCoursesError),
    );
  }, [requestingCourses, allCoursesLoaded, listCoursesError]);

  useEffect(() => {
    if (!allCoursesLoaded) {
      dispatch(Actions.requestCoursesList());
    }
  }, [allCoursesLoaded, dispatch]);

  useEffect(() => {
    setCoursesArr(sortArrByKey(convertObjToArr(coursesObj), 'name'));
  }, [coursesObj]);

  useEffect(() => {
    if (coursesArr.length === 1) {
      setActiveCourse(coursesArr[0].id);
    }
    coursesArr.forEach(course => {
      if (course.canComment) {
        setProductWithComments(true);
      }
    });
  }, [coursesArr]);

  const handleChangeCourse = useCallback(newActiveCourse => {
    setActiveCourse(oldActiveCourse =>
      newActiveCourse !== oldActiveCourse ? newActiveCourse : 0,
    );
  }, []);

  const handleChangeTab = useCallback((event, newActiveTab) => {
    setActiveTab(newActiveTab);
  }, []);

  const handleRetryLoadListCourses = useCallback(() => {
    dispatch(Actions.requestCoursesList());
  }, [dispatch]);

  return (
    <Box className={classes.container}>
      <Helmet>
        <title>{`Visão geral - ${productName}`}</title>
      </Helmet>
      <SectionCard className="section progress-container" title={progressLabel}>
        <Box className="progress-content">
          {loadingCourses && <Course loading />}
          {!!listCoursesError && !loadingCourses && (
            <MsgBox
              type="WARNING"
              msg="Não foi possível carregar seu progresso"
              buttonText="Tentar novamente"
              buttonAction={handleRetryLoadListCourses}
            />
          )}
          {!loadingCourses && !listCoursesError && !coursesArr.length && (
            <MsgBox
              type="INFO"
              icon="search_off"
              msg="Você ainda não possuí treinamentos disponíveis"
            />
          )}
          {coursesArr.map(course => (
            <Course
              key={course.id}
              active={activeCourse === course.id}
              onActive={handleChangeCourse}
              studentProfileData={studentProfileData}
              {...course}
            />
          ))}
        </Box>
      </SectionCard>
      <SectionCard className="section tabs-container">
        <Tabs
          classes={{ flexContainer: classes.tabContainer }}
          value={activeTab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <TabTitle
            title="Anotações"
            ammount={annotationsAmmount}
            index={0}
            active={activeTab}
          />
          {productWithComments && (
            <TabTitle
              title="Comentários"
              ammount={commentsAmmount}
              index={1}
              active={activeTab}
            />
          )}
        </Tabs>
        <Box className="tabs-content">
          <TabPanel
            className={activeTab === 0 ? 'tabs-panel' : ''}
            active={activeTab}
            index={0}
          >
            <Notes />
          </TabPanel>
          {productWithComments && (
            <TabPanel
              className={activeTab === 1 ? 'tabs-panel' : ''}
              active={activeTab}
              index={1}
            >
              <Comments />
            </TabPanel>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
};

export default Dashboard;
